/**
 * Full reply form shown inside the Reply drawer — modelled on the KC2 reference
 * reply panel: channel tabs, To, Category / Sub Category, Quick Reply
 * (WhatsApp templates), a Public/Private privacy toggle, a rich-text Message
 * editor, and an attachments dropzone.
 *
 * A kc3 conversation has a single channel, so only that channel's tab(s) are
 * shown (e.g. an SMS conversation shows just "SMS Reply"); other channels are hidden.
 * Facebook shows both Comment and Messenger tabs. Sending posts to
 * POST /conversations/:id/messages on the conversation's channel.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { AtSign, Eye, FileText, Globe, Mail, MessageCircle, MessageSquare, Paperclip, Send, Users, Video, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useUploadAttachment } from '../../../hooks/useUploadAttachment.js'
import { useWhatsAppTemplates } from '../../../hooks/useWhatsAppTemplates.js'
import { useSuggestedArticles } from '../../../hooks/useSuggestedArticles.js'
import { renderTemplate } from '../../../utils/templateVars.js'
import RichTextEditor, { isEmptyHtml } from '../../common/RichTextEditor.js'
import AiComposeBar from './ai/AiComposeBar.js'
import ReplyPreviewDialog from './ReplyPreviewDialog.js'
import { useReplySubject } from './useReplySubject.js'
import EmailChipsInput, { hasInvalidRecipient } from '../../common/EmailChipsInput.js'
import type { ConversationDetailResponse, Message } from '../../../modules/conversations/types.js'
import { cn } from '../../../utils/cn.js'
import { Field, GhostButton, PrimaryButton, R, SelectField, htmlToText, inputClass } from './primitives.js'

type Conversation = ConversationDetailResponse['conversation']

function AttachmentPreview({ file, fallbackUrl }: { file: File; fallbackUrl: string | undefined }): JSX.Element | null {
  const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(file.name)
  const [dataUrl, setDataUrl] = useState<string | undefined>(fallbackUrl)

  useEffect(() => {
    if (!isImage || fallbackUrl) return
    let cancelled = false
    const reader = new FileReader()
    reader.onload = () => { if (!cancelled) setDataUrl(reader.result as string) }
    reader.readAsDataURL(file)
    return () => { cancelled = true }
  }, [file, isImage, fallbackUrl])

  if (!isImage) return <FileText className="h-5 w-5 shrink-0 text-slate-400" />
  if (!dataUrl) return <div className="h-10 w-10 animate-spin rounded-full border border-slate-300 border-t-slate-600" />
  return <img src={dataUrl} alt={file.name} className="h-10 w-10 rounded-[3px] object-cover" />
}

const CHANNEL_TABS: { key: string; label: string; icon: LucideIcon; matches: (c: string) => boolean }[] = [
  { key: 'web', label: 'Web Reply', icon: Globe, matches: (c) => c === 'web' || c === 'chat' || c === '' },
  // Web (agent-portal) tickets also get it when the ticket has an email
  // address — see `activeTabs`.
  { key: 'email', label: 'Email Reply', icon: Mail, matches: (c) => c === 'email' },
  { key: 'sms', label: 'SMS Reply', icon: MessageSquare, matches: (c) => c === 'sms' },
  { key: 'twitter', label: 'Twitter Reply', icon: AtSign, matches: (c) => c === 'twitter' || c === 'x' },
  { key: 'facebook', label: 'Comment Reply', icon: Users, matches: (c) => c === 'facebook' },
  { key: 'messenger', label: 'Messenger Reply', icon: MessageCircle, matches: (c) => c === 'facebook' },
  { key: 'youtube', label: 'Youtube Reply', icon: Video, matches: (c) => c === 'youtube' },
  { key: 'whatsapp', label: 'WhatsApp Reply', icon: MessageCircle, matches: (c) => c === 'whatsapp' },
]

interface PendingAttachment {
  localId: string
  name: string
  state: 'uploading' | 'uploaded' | 'error'
  attachmentId?: string
  mimeType?: string
  previewUrl: string | undefined
  file?: File
}

interface SendResponse {
  messageId: string
  /**
   * Status the server stored. 'sent' for an internal note (nothing delivers
   * one), 'queued' for a customer message awaiting dispatch.
   */
  status?: 'pending_approval' | 'queued' | 'sent'
}

export interface ReplyFormProps {
  conversation: Conversation
  currentAgentId: string | null
  initialMode: 'reply' | 'note'
  canEdit: boolean
  onPatch: (patch: Record<string, unknown>) => Promise<boolean>
  onSent: () => void
  appendMessage: (m: Message) => void
  replaceMessage: (oldId: string, m: Message) => void
  markMessageFailed: (id: string, error: string) => void
}

export default function ReplyForm(props: ReplyFormProps): JSX.Element {
  const { conversation } = props
  const isFacebook = conversation.channelOrigin === 'facebook'
  // For Facebook, both 'facebook' (Comment Reply) and 'messenger' (Messenger Reply) tabs are active.
  const isWebTicket = conversation.channelOrigin === 'web'
  const webEmail = isWebTicket ? (conversation.clientEmail ?? null) : null
  const activeTabs = useMemo(
    () => CHANNEL_TABS.filter((t) => t.matches(conversation.channelOrigin) || (t.key === 'email' && !!webEmail)),
    [conversation.channelOrigin, webEmail],
  )
  // A web ticket with an email address starts on Email Reply — before the
  // choice existed every such reply was mailed to the client.
  const [selectedTab, setSelectedTab] = useState(
    webEmail ? 'email' : (activeTabs[0]?.key ?? 'web'),
  )
  const activeKey = selectedTab
  const isWhatsApp = conversation.channelOrigin === 'whatsapp'
  const isYouTube = conversation.channelOrigin === 'youtube'
  /** Email recipients UI: email tickets, and a web ticket's Email Reply tab. */
  const isEmail = conversation.channelOrigin === 'email' || (isWebTicket && selectedTab === 'email')
  const isMessengerReply = isFacebook && selectedTab === 'messenger'

  const [privacy, setPrivacy] = useState<'public' | 'private'>(
    props.initialMode === 'note' ? 'private' : 'public',
  )
  // Email replies address a list (To/Cc/Bcc chips); every other channel has a
  // single recipient, so it keeps the plain text field.
  const [to, setTo] = useState(conversation.primaryHandle ?? '')
  const [toList, setToList] = useState<string[]>(
    webEmail ? [webEmail] : conversation.primaryHandle && !isWebTicket ? [conversation.primaryHandle] : [],
  )
  // A web ticket's email reply keeps the Cc/Bcc the ticket was created with.
  const [ccList, setCcList] = useState<string[]>(isWebTicket ? (conversation.creationCc ?? []) : [])
  const [bccList, setBccList] = useState<string[]>(isWebTicket ? (conversation.creationBcc ?? []) : [])
  const [showCc, setShowCc] = useState(ccList.length > 0)
  const [showBcc, setShowBcc] = useState(bccList.length > 0)
  const replySubject = useReplySubject(conversation.id, isEmail)
  const [message, setMessage] = useState('')
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  const fileRef = useRef<HTMLInputElement>(null)
  const { upload } = useUploadAttachment()
  const { templates } = useWhatsAppTemplates()
  const { articles: suggestedArticles, loading: suggestionsLoading } = useSuggestedArticles(conversation.id)
  const [insertedArticleIds, setInsertedArticleIds] = useState<Set<string>>(new Set())

  const handleInsertArticle = async (articleId: string): Promise<void> => {
    const article = await api.get<{ contentHtml: string }>(`/knowledge-base/articles/${articleId}`)
    setMessage((prev) => `${prev}${article.contentHtml}`)
    setInsertedArticleIds((prev) => new Set(prev).add(articleId))
  }

  const visibleSuggestions = suggestedArticles.filter((a) => !insertedArticleIds.has(a.id))

  const isPublic = privacy === 'public'
  const plainLen = htmlToText(message).length
  const uploading = pending.some((p) => p.state === 'uploading')
  // A public email reply needs at least one To address, and every chip on any
  // of the three lists must be a valid address.
  const recipientsInvalid =
    isEmail &&
    isPublic &&
    (toList.length === 0 ||
      hasInvalidRecipient(toList) ||
      hasInvalidRecipient(ccList) ||
      hasInvalidRecipient(bccList))
  const canSend =
    !sending &&
    !uploading &&
    !recipientsInvalid &&
    (!isEmptyHtml(message) || attachmentIds.length > 0) &&
    (!isPublic || plainLen >= 10)

  /* ── Attachments ── */
  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      const list = Array.from(files)
      const additions: PendingAttachment[] = list.map((f) => {
        const isImage = f.type.startsWith('image/') || /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(f.name)
        return {
          localId: crypto.randomUUID(),
          name: f.name,
          state: 'uploading',
          mimeType: f.type || (isImage ? 'image/unknown' : 'application/octet-stream'),
          previewUrl: undefined,
          file: f,
        }
      })
      setPending((prev) => [...prev, ...additions])
      additions.forEach((a, i) => {
        const file = list[i]
        if (!file) return
        void (async () => {
          try {
            const res = await upload(file)
            const isImage = res.mimeType.startsWith('image/')
            setPending((prev) => prev.map((p) => (p.localId === a.localId ? {
              ...p,
              state: 'uploaded',
              attachmentId: res.attachmentId,
              mimeType: res.mimeType,
              previewUrl: isImage ? `/api/attachments/${res.attachmentId}` : undefined,
            } : p)))
            setAttachmentIds((prev) => [...prev, res.attachmentId])
          } catch {
            setPending((prev) => prev.map((p) => (p.localId === a.localId ? { ...p, state: 'error' } : p)))
          }
        })()
      })
    },
    [upload],
  )

  const removeAttachment = (localId: string): void => {
    setPending((prev) => {
      const removed = prev.find((p) => p.localId === localId)
      if (removed?.attachmentId) setAttachmentIds((ids) => ids.filter((id) => id !== removed.attachmentId))
      return prev.filter((p) => p.localId !== localId)
    })
  }

  /* ── Send ── */
  async function submit(): Promise<void> {
    if (!canSend) return
    setError(null)
    const html = message
    const text = htmlToText(html)
    const visibility = isPublic ? 'customer' : 'internal'
    const tempId = `optimistic-${crypto.randomUUID()}`
    const optimistic: Message = {
      id: tempId,
      conversationId: conversation.id,
      direction: 'outbound',
      senderHandle: null,
      senderAgentId: props.currentAgentId,
      senderAgentName: null,
      senderName: null,
      body: text,
      visibility,
      // Show the real envelope right away; without it the timeline fell back
      // to the customer's address until the thread was reloaded.
      ...(isEmail && isPublic
        ? {
          toRecipients: [...toList],
          ...(ccList.length ? { ccRecipients: [...ccList] } : {}),
          ...(bccList.length ? { bccRecipients: [...bccList] } : {}),
          ...(replySubject.customSubject ? { emailSubject: replySubject.customSubject } : {}),
        }
        : {}),
      attachments: attachmentIds.map((id) => ({ id, mimeType: 'application/octet-stream', filename: 'attachment', sizeBytes: 0 })),
      status: 'sending',
      createdAt: new Date().toISOString(),
    }
    props.appendMessage(optimistic)
    setSending(true)
    try {
      const resp = await api.post<SendResponse>(`/conversations/${conversation.id}/messages`, {
        contentText: text,
        contentHtml: html,
        visibility,
        // Email sends the full chip lists; other channels keep the single handle.
        ...(isEmail && isPublic
          ? {
            ...(toList.length ? { recipientHandles: toList } : {}),
            ...(ccList.length ? { cc: ccList } : {}),
            ...(bccList.length ? { bcc: bccList } : {}),
            ...(replySubject.customSubject ? { emailSubject: replySubject.customSubject } : {}),
          }
          : to.trim() && isPublic && !isYouTube && !(isFacebook && !isMessengerReply)
            ? { recipientHandle: to.trim() }
            : {}),
        ...(isFacebook && isPublic ? { provider: isMessengerReply ? 'messenger' : 'facebook_comment' } : {}),
        // Web ticket: the tab decides whether the client also gets an email.
        ...(isWebTicket && isPublic ? { replyVia: selectedTab === 'email' ? 'email' : 'web' } : {}),
        ...(attachmentIds.length ? { attachments: attachmentIds } : {}),
      })
      props.replaceMessage(tempId, {
        ...optimistic,
        id: resp.messageId,
        // Use what the server actually stored. An internal note is already
        // 'sent' — assuming 'queued' left it showing "Sending" forever, since
        // nothing delivers a note and no status event ever follows.
        status: resp.status ?? 'queued',
      })
      props.onSent()
    } catch (err) {
      props.markMessageFailed(tempId, err instanceof Error ? err.message : 'Send failed')
      setError(err instanceof Error ? err.message : 'Send failed')
    } finally {
      setSending(false)
    }
  }

  const approvedTemplates = templates.filter((t) => t.status === 'APPROVED')

  return (
    <div className="flex h-full flex-col">
      {/* Channel tabs — only the tab(s) valid for THIS conversation's channel
          are shown (e.g. an SMS conversation shows only "SMS Reply"). Other channels
          are hidden, not disabled. Facebook shows both Comment + Messenger. */}
      <div className="scrollbar-hide flex shrink-0 items-center gap-1 overflow-x-auto border-b border-slate-200 px-5">
        {activeTabs.map((t) => {
          const active = t.key === activeKey
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setSelectedTab(t.key)}
              title={t.label}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold',
                active
                  ? 'border-[#3366FF] text-[#3366FF]'
                  : 'cursor-pointer border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          )
        })}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {/* Recipients — email gets chip lists plus Cc/Bcc; the rest keep the
            single free-text handle they always had. */}
        {isPublic && isEmail && (
          <>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="block text-xs font-semibold text-slate-600">
                  To<span className="ml-0.5 text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {!showCc && (
                    <button
                      type="button"
                      onClick={() => setShowCc(true)}
                      className="text-xs font-semibold text-slate-500 hover:text-[#3366FF]"
                    >
                      Cc
                    </button>
                  )}
                  {!showBcc && (
                    <button
                      type="button"
                      onClick={() => setShowBcc(true)}
                      className="text-xs font-semibold text-slate-500 hover:text-[#3366FF]"
                    >
                      Bcc
                    </button>
                  )}
                </div>
              </div>
              <EmailChipsInput
                value={toList}
                onChange={setToList}
                ariaLabel="To recipients"
                placeholder="name@example.com — press Enter to add"
              />
              {toList.length === 0 && (
                <p className="mt-1 text-[11px] text-slate-400">Add at least one recipient.</p>
              )}
              {hasInvalidRecipient(toList) && (
                <p className="mt-1 text-[11px] text-red-500">Remove or fix the invalid address.</p>
              )}
            </div>

            {showCc && (
              <Field
                label="Cc"
                error={hasInvalidRecipient(ccList) ? 'Remove or fix the invalid address.' : undefined}
              >
                <EmailChipsInput
                  value={ccList}
                  onChange={setCcList}
                  autoFocus
                  ariaLabel="Cc recipients"
                  placeholder="Everyone on the thread can see Cc"
                />
              </Field>
            )}

            {showBcc && (
              <Field
                label="Bcc"
                hint="Hidden from all other recipients"
                error={hasInvalidRecipient(bccList) ? 'Remove or fix the invalid address.' : undefined}
              >
                <EmailChipsInput
                  value={bccList}
                  onChange={setBccList}
                  autoFocus
                  ariaLabel="Bcc recipients"
                  placeholder="name@example.com — press Enter to add"
                />
              </Field>
            )}

            <Field label="Subject" hint="The ticket reference is added automatically">
              <input
                value={replySubject.subject}
                onChange={(e) => replySubject.setSubject(e.target.value)}
                maxLength={998}
                aria-label="Subject"
                placeholder={replySubject.defaultSubject || 'Subject'}
                className={inputClass}
              />
            </Field>
          </>
        )}

        {isPublic && !isEmail && !isYouTube && !(isFacebook && !isMessengerReply) && (
          <Field label="To">
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient" className={inputClass} />
          </Field>
        )}

        {!suggestionsLoading && visibleSuggestions.length > 0 && (
          <div className="mb-3 rounded-[3px] border border-indigo-100 bg-indigo-50/40 p-3">
            <p className="mb-2 text-xs font-semibold text-indigo-700">Suggested Articles</p>
            <div className="space-y-2">
              {visibleSuggestions.map((article) => (
                <div key={article.id} className="flex items-center justify-between gap-3 rounded-[3px] bg-white px-3 py-2 shadow-sm">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{article.title}</p>
                    <p className="text-xs text-slate-400">{article.category} · {Math.round(article.score * 100)}% match</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleInsertArticle(article.id)}
                    className="shrink-0 rounded-[3px] bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                  >
                    Insert into reply
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {isWhatsApp && (
          <Field label="Quick Reply" hint="Insert an approved WhatsApp template">
            <SelectField
              value=""
              onChange={(id) => {
                const t = approvedTemplates.find((x) => x.id === id)
                if (t) setMessage((prev) => `${prev}${renderTemplate(t.bodyText, [])}`)
              }}
              placeholder="Select Quick Reply"
              options={approvedTemplates.map((t) => ({ value: t.id, label: t.metaTemplateName }))}
            />
          </Field>
        )}

        {/* Privacy */}
        <Field label="Privacy">
          <div className="inline-flex overflow-hidden rounded-[3px] border border-slate-200">
            {(['public', 'private'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPrivacy(p)}
                className={cn(
                  'px-4 py-1.5 text-sm font-semibold capitalize transition-colors',
                  privacy === p
                    ? p === 'private'
                      ? 'bg-amber-500 text-white'
                      : 'bg-[#3366FF] text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </Field>

        {/* Message */}
        <Field
          label={`Message${isPublic ? ' (minimum 10 characters)' : ''}`}
          required={isPublic}
          error={error ?? undefined}
        >
          <RichTextEditor value={message} onChange={setMessage} height={220} placeholder="Write your reply…" />
          <AiComposeBar value={message} onChange={setMessage} html disabled={sending} />
        </Field>

        {/* Attachments */}
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Attachments</label>
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              addFiles(e.dataTransfer.files)
            }}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1 ${R} border border-dashed border-slate-300 bg-slate-50 px-3 py-5 text-center transition-colors hover:border-[#3366FF] hover:bg-[#F5F7FF]`}
          >
            <Paperclip className="h-5 w-5 text-slate-400" />
            <span className="text-xs text-slate-500">Drag &amp; drop files here, or click to select</span>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
          </label>
          {pending.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {pending.map((p) => {
                const isImage = p.mimeType?.startsWith('image/') ?? false
                return (
                  <li key={p.localId} className={`flex items-center justify-between ${R} bg-slate-100 px-2.5 py-1.5 text-xs text-slate-700`}>
                    <span className="flex items-center gap-2 truncate">
                      {isImage && p.file ? (
                        <AttachmentPreview file={p.file} fallbackUrl={p.previewUrl} />
                      ) : isImage && p.previewUrl ? (
                        <img src={p.previewUrl} alt={p.name} className={`h-6 w-6 ${R} object-cover`} />
                      ) : (
                        <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                      <span className="truncate">{p.name}</span>
                      {p.state === 'uploading' && <span className="h-2.5 w-2.5 animate-spin rounded-full border border-slate-300 border-t-slate-600" />}
                      {p.state === 'error' && <span className="text-red-500">failed</span>}
                    </span>
                    <button type="button" onClick={() => removeAttachment(p.localId)} className="text-slate-400 hover:text-slate-600">
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3.5">
        <GhostButton onClick={props.onSent}>Cancel</GhostButton>
        {/* Preview only makes sense for a public reply — an internal note is
            never templated or delivered to the customer. */}
        {isPublic && (
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            disabled={isEmptyHtml(message)}
            title={isEmptyHtml(message) ? 'Write a reply to preview it' : 'See how this reply reaches the customer'}
            className={`inline-flex items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
        )}
        <PrimaryButton onClick={() => void submit()} disabled={!canSend}>
          <span className="flex items-center gap-1.5">
            <Send className="h-4 w-4" />
            {sending ? 'Sending…' : isPublic ? 'Send' : 'Add note'}
          </span>
        </PrimaryButton>
      </div>

      <ReplyPreviewDialog
        open={previewOpen}
        conversationId={conversation.id}
        contentHtml={message}
        emailSubject={isEmail && isPublic ? replySubject.customSubject : undefined}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  )
}
